import { useEffect, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Pagination } from "@/components/ui/pagination"
import { api, authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"
import { PencilIcon, PlusIcon, SearchIcon, Trash2Icon } from "lucide-react"
import { RestoreIcon } from "@/components/icons/RestoreIcon"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const PAGE_SIZE = 25

interface ServiceTypeRow {
  id: string
  name: string
  code: string
  deletedAt: string | null
  createdAt: string
  updatedAt: string
}

const EMPTY_FORM = {
  name: "",
  code: "",
}

type FilterStatus = "active" | "deleted" | "all"

type FormState = typeof EMPTY_FORM

export default function ServiceTypesPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()

  const [search, setSearch] = useState("")
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active")
  const [page, setPage] = useState(1)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [editing, setEditing] = useState<ServiceTypeRow | null>(null)
  const [deleting, setDeleting] = useState<ServiceTypeRow | null>(null)
  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM })
  const [formError, setFormError] = useState<string | null>(null)

  // ── Query ────────────────────────────────────────────────────────────────
  const { data: serviceTypes = [], isLoading } = useQuery({
    queryKey: ["trips", "service-types", filterStatus],
    queryFn: async () => {
      const query: Record<string, string> = {}
      if (filterStatus === "active") query.deleted = "false"
      else if (filterStatus === "deleted") query.deleted = "true"

      const res = await api.trips["service-types"].$get({ query })
      if (!res.ok) throw new Error("Error loading service types")
      return res.json() as Promise<ServiceTypeRow[]>
    },
  })

  // ── Derived data ────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim()
    return serviceTypes.filter((item) => {
      if (!term) return true
      return (
        item.name.toLowerCase().includes(term) ||
        item.code.toLowerCase().includes(term)
      )
    })
  }, [serviceTypes, search])

  useEffect(() => { setPage(1) }, [search, filterStatus])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  // ── Mutations ────────────────────────────────────────────────────────────
  const createMut = useMutation({
    mutationFn: async (data: FormState) => {
      const res = await api.trips["service-types"].$post({
        json: {
          name: data.name,
          code: data.code,
        },
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al crear tipo de servicio")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips", "service-types"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FormState }) => {
      const res = await api.trips["service-types"][":id"].$patch({
        param: { id },
        json: {
          name: data.name,
          code: data.code,
        },
      })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al actualizar tipo de servicio")
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips", "service-types"] })
      closeSheet()
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.trips["service-types"][":id"].$delete({ param: { id } })
      if (!res.ok) {
        const body = await res.json() as { error?: string }
        throw new Error(body.error ?? "Error al eliminar tipo de servicio")
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips", "service-types"] })
      setDeleteOpen(false)
      setDeleting(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  const restoreMut = useMutation({
    mutationFn: async (id: string) => {
      await authorizedFetch(`${API_URL}/api/trips/service-types/${id}/restore`, {
        method: "PATCH",
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trips", "service-types"] })
    },
  })

  // ── Helpers ─────────────────────────────────────────────────────────────
  function closeSheet() {
    setSheetOpen(false)
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
  }

  function openCreate() {
    setEditing(null)
    setForm({ ...EMPTY_FORM })
    setFormError(null)
    setSheetOpen(true)
  }

  function openEdit(item: ServiceTypeRow) {
    setEditing(item)
    setForm({ name: item.name, code: item.code })
    setFormError(null)
    setSheetOpen(true)
  }

  function openDelete(item: ServiceTypeRow) {
    setDeleting(item)
    setDeleteOpen(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    const payload: FormState = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
    }

    if (!payload.name || !payload.code) {
      setFormError("Completá nombre y código")
      return
    }

    if (editing) updateMut.mutate({ id: editing.id, data: payload })
    else createMut.mutate(payload)
  }

  const isSaving = createMut.isPending || updateMut.isPending

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <>
      <SiteHeader title="Tipos de servicio" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por código o nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>

          <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as FilterStatus)}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Activos</SelectItem>
              <SelectItem value="deleted">Eliminados</SelectItem>
              <SelectItem value="all">Todos</SelectItem>
            </SelectContent>
          </Select>

          {can("write", "trips") && (
            <Button onClick={openCreate} className="gap-2">
              <PlusIcon className="size-4" /> Nuevo tipo
            </Button>
          )}
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Código</TableHead>
                <TableHead>Nombre</TableHead>
                {can("write", "trips") && <TableHead className="w-32 text-right pr-4">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={can("write", "trips") ? 3 : 2} className="text-center py-16 text-muted-foreground">
                    Cargando tipos de servicio...
                  </TableCell>
                </TableRow>
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={can("write", "trips") ? 3 : 2} className="text-center py-16 text-muted-foreground">
                    {search ? "No results for the search" : "No service types found"}
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((item) => (
                  <TableRow key={item.id} className={item.deletedAt ? "opacity-70" : undefined}>
                    <TableCell>
                      <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded uppercase tracking-wide">
                        {item.code}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium text-sm">{item.name}</TableCell>
                    {can("write", "trips") && (
                      <TableCell className="text-right pr-4">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openEdit(item)}
                            title="Editar"
                          >
                            <PencilIcon className="size-3.5" />
                          </Button>
                          {item.deletedAt ? (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => restoreMut.mutate(item.id)}
                              disabled={restoreMut.isPending}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Restaurar"
                            >
                              <RestoreIcon className="size-3.5" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => openDelete(item)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={filtered.length}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      <Sheet open={sheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); else setSheetOpen(true) }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar tipo de servicio" : "Nuevo tipo de servicio"}</SheetTitle>
            <SheetDescription>Mantené códigos cortos y descriptivos.</SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">
            <div className="grid gap-1.5">
              <Label htmlFor="st-code">Código</Label>
              <Input
                id="st-code"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                maxLength={10}
                className="uppercase font-mono"
                placeholder="ECO"
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="st-name">Nombre</Label>
              <Input
                id="st-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={100}
                placeholder="Servicio ecológico"
                required
              />
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? "Guardando..." : editing ? "Guardar cambios" : "Crear tipo"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar tipo de servicio</DialogTitle>
            <DialogDescription>
              ¿Eliminás el tipo <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible desde la opción de restaurar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
